import React from "react";
import { Partner } from "../../models/Partner";
import { toCamelCase } from "../../hooks/toCameCase.tsx";
import { notification } from "../../utils/Notifications.tsx";

const PANEL_SUMMARY_STYLE_ID = "profile-partner-panel-summary-styles-v5";

const ProfilePartnerPanelSummary = ({
  partner,
  placement = "panel",
}: {
  partner: Partner;
  placement?: "panel" | "identity";
}) => {
  const highRisk = Boolean(partner?.candidate?.vacancy?.position?.highRisk);

  React.useEffect(() => {
    [
      "profile-partner-panel-summary-styles",
      "profile-partner-panel-summary-styles-v2",
      "profile-partner-panel-summary-styles-v3",
      "profile-partner-panel-summary-styles-v4",
    ].forEach((id) => document.getElementById(id)?.remove());
    if (document.getElementById(PANEL_SUMMARY_STYLE_ID)) return;
    const styleSheet = document.createElement("style");
    styleSheet.id = PANEL_SUMMARY_STYLE_ID;
    styleSheet.textContent = `
      .profile-partner-panel__summary {
        flex-shrink: 0;
        padding: 18px 22px;
        border-bottom: 1px solid #f1f5f9;
        background: #ffffff;
      }
      .profile-partner-panel__summary-top {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 16px;
      }
      .profile-partner-panel__summary-name {
        margin: 0;
        font-size: 1.25rem;
        font-weight: 800;
        color: #0f172a;
        line-height: 1.25;
        letter-spacing: -0.02em;
      }
      .profile-partner-panel__summary-role {
        margin: 6px 0 0;
        font-size: 0.8125rem;
        font-weight: 500;
        color: #64748b;
      }
      .profile-partner-panel__summary-badges {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: flex-end;
        gap: 8px;
        flex-shrink: 0;
      }
      .profile-partner-panel__clock {
        padding: 9px 16px;
        border-radius: 999px;
        font-size: 13px;
        font-weight: 600;
        color: #334155;
        background: #f1f5f9;
        border: 1px solid #e2e8f0;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        transition: background 0.15s ease, border-color 0.15s ease;
      }
      .profile-partner-panel__clock i {
        font-size: 14px;
      }
      .profile-partner-panel__clock-number {
        font-size: 15px;
        font-weight: 800;
        letter-spacing: 0.02em;
      }
      .profile-partner-panel__clock:hover {
        background: #e2e8f0;
        border-color: #cbd5e1;
      }
      .profile-header-risk-badge {
        padding: 9px 16px;
        border-radius: 999px;
        font-size: 13px;
        font-weight: 700;
        display: inline-flex;
        align-items: center;
        gap: 8px;
      }
      .profile-header-risk-badge i {
        font-size: 14px;
      }
      .profile-header-risk-badge--high {
        background: #fef2f2;
        color: #b91c1c;
        border: 1px solid #fecaca;
      }
      .profile-header-risk-badge--low {
        background: #f0fdf4;
        color: #15803d;
        border: 1px solid #bbf7d0;
      }
      @media (max-width: 575.98px) {
        .profile-partner-panel__summary--panel {
          padding: 14px 12px;
        }
        .profile-partner-panel__summary--panel .profile-partner-panel__summary-top {
          flex-direction: column;
          align-items: stretch;
          gap: 12px;
        }
      }
    `;
    document.head.appendChild(styleSheet);
  }, []);

  const fullName = toCamelCase(
    `${partner.person.firstName} ${partner.person.lastName} ${partner.person.secondLastName}`
  );
  const positionName = partner?.candidate?.vacancy?.position?.name ?? "";
  const branchName = partner?.candidate?.branch?.name ?? "";
  const subtitle = [positionName, branchName].filter(Boolean).join(" · ");

  const copyClockNumber = () => {
    navigator.clipboard.writeText(String(partner.clockNumber));
    notification(
      "Copiado",
      "Número de reloj checador copiado al portapapeles",
      "success"
    );
  };

  const infoBlock = (
    <div className="profile-partner-panel__summary-info" style={{ minWidth: 0 }}>
      <h2 className="profile-partner-panel__summary-name">{fullName}</h2>
      {subtitle ? (
        <p className="profile-partner-panel__summary-role">{subtitle}</p>
      ) : null}
    </div>
  );

  const badgesBlock = (
    <div className="profile-partner-panel__summary-badges">
      <div
        className={`profile-header-risk-badge ${
          highRisk
            ? "profile-header-risk-badge--high"
            : "profile-header-risk-badge--low"
        }`}
      >
        <i
          className={`fa-solid ${
            highRisk ? "fa-triangle-exclamation" : "fa-circle-check"
          }`}
        />
        {highRisk ? "Alto riesgo" : "Sin riesgo"}
      </div>
      {partner.clockNumber != null ? (
        <div
          className="profile-partner-panel__clock"
          onClick={copyClockNumber}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") copyClockNumber();
          }}
          title="Copiar número de reloj checador"
        >
          <i className="fa-solid fa-clock" />
          <span>
            <strong>Reloj:</strong>{" "}
            <span className="profile-partner-panel__clock-number">
              {partner.clockNumber}
            </span>
          </span>
          <i className="fa-solid fa-copy" style={{ opacity: 0.55, fontSize: 12 }} />
        </div>
      ) : null}
    </div>
  );

  if (placement === "identity") {
    return (
      <div
        className={`profile-partner-panel__summary profile-partner-panel__summary--${placement}`}
      >
        {infoBlock}
        {badgesBlock}
      </div>
    );
  }

  return (
    <div
      className={`profile-partner-panel__summary profile-partner-panel__summary--${placement}`}
    >
      <div className="profile-partner-panel__summary-top">
        {infoBlock}
        {badgesBlock}
      </div>
    </div>
  );
};

export default ProfilePartnerPanelSummary;
